import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { ChevronLeft, History, Pencil, Printer, Save, Settings2, Trash2 } from "lucide-react"
import { useState } from "react"
import { Link, useNavigate, useParams } from "react-router"

import { api, ErrorApi } from "@/api/cliente"
import { Documento, type NodoDoc } from "@/componentes/documentos/Documento"
import { EditorDocumento } from "@/componentes/documentos/EditorDocumento"
import { MetadatosDocumento } from "@/componentes/documentos/MetadatosDocumento"
import { PortadaDocumento } from "@/componentes/documentos/PortadaDocumento"
import type { Documento as Doc, EstadoDoc, VersionDoc } from "@/componentes/documentos/tipos"
import { ESTADOS_DOC } from "@/componentes/documentos/tipos"
import { VersionesDocumento } from "@/componentes/documentos/VersionesDocumento"
import { EncabezadoEmpresa } from "@/componentes/empresa/EncabezadoEmpresa"
import { Boton } from "@/componentes/ui/Boton"
import { tienePermiso } from "@/lib/autorizacion"
import { usarSesion } from "@/lib/sesion"

/**
 * Un documento controlado: leerlo, redactarlo y publicar su versión.
 *
 * ⚠ **Redactar y aprobar son botones distintos porque son permisos distintos.** Quien
 * escribe el procedimiento no es necesariamente quien responde de que la agencia lo
 * siga: `biblioteca:editar` escribe, `biblioteca:aprobar` declara vigente y publica.
 *
 * ⚠ **«Exportar PDF» es la impresión del navegador, y es lo correcto.** Sale texto
 * vectorial —seleccionable, buscable, con las tablas bien— en vez de una captura de
 * pantalla ampliada. La hoja se prepara con `@media print` y `.no-print`.
 */

export function DocumentoSGI() {
  const { id } = useParams()
  const navegar = useNavigate()
  const clienteQuery = useQueryClient()
  const usuario = usarSesion((e) => e.usuario)
  const puedeEditar = tienePermiso(usuario, "biblioteca:editar")
  const puedeAprobar = tienePermiso(usuario, "biblioteca:aprobar")

  const [editando, setEditando] = useState(false)
  const [borrador, setBorrador] = useState<NodoDoc | null>(null)
  const [verMetadatos, setVerMetadatos] = useState(false)
  const [verVersiones, setVerVersiones] = useState(false)

  const documento = useQuery({
    queryKey: ["biblioteca", "documento", id],
    queryFn: () => api.get<Doc>(`/api/v1/biblioteca/documentos/${id}`),
    enabled: id !== undefined,
  })

  const versiones = useQuery({
    queryKey: ["biblioteca", "versiones", id],
    queryFn: () => api.get<VersionDoc[]>(`/api/v1/biblioteca/documentos/${id}/versiones`),
    enabled: id !== undefined,
  })

  const refrescar = () => {
    clienteQuery.invalidateQueries({ queryKey: ["biblioteca", "documento", id] })
    clienteQuery.invalidateQueries({ queryKey: ["biblioteca", "documentos"] })
  }

  const guardar = useMutation({
    mutationFn: () =>
      api.put<Doc>(`/api/v1/biblioteca/documentos/${id}/cuerpo`, { cuerpo: borrador }),
    onSuccess: () => {
      refrescar()
      setEditando(false)
    },
  })

  const cambiarEstado = useMutation({
    mutationFn: (estado: EstadoDoc) =>
      api.put<Doc>(`/api/v1/biblioteca/documentos/${id}/estado`, { estado }),
    onSuccess: refrescar,
  })

  const eliminar = useMutation({
    mutationFn: () => api.delete(`/api/v1/biblioteca/documentos/${id}`),
    onSuccess: () => navegar("/planificacion/biblioteca"),
  })

  if (documento.isPending) {
    return (
      <p className="text-sm" style={{ color: "var(--fg-muted)" }}>
        Cargando el documento…
      </p>
    )
  }

  if (documento.isError || !documento.data) {
    return (
      <p role="alert" className="text-sm" style={{ color: "var(--destructive)" }}>
        No se pudo cargar el documento
      </p>
    )
  }

  const doc = documento.data

  return (
    <div>
      <div className="no-print mb-6 flex flex-wrap items-center gap-2">
        <Link
          to="/planificacion/biblioteca"
          className="flex items-center gap-1 text-sm"
          style={{ color: "var(--fg-muted)" }}
        >
          <ChevronLeft size={16} aria-hidden="true" />
          Biblioteca
        </Link>

        <div className="ml-auto flex flex-wrap items-center gap-2">
          {editando ? (
            <>
              <Boton
                variante="fantasma"
                onClick={() => {
                  setBorrador(doc.cuerpo)
                  setEditando(false)
                }}
              >
                Cancelar
              </Boton>
              <Boton
                variante="primario"
                onClick={() => guardar.mutate()}
                disabled={guardar.isPending}
              >
                <Save size={15} aria-hidden="true" />
                {guardar.isPending ? "Guardando…" : "Guardar"}
              </Boton>
            </>
          ) : (
            <>
              <Boton variante="fantasma" onClick={() => setVerVersiones(true)}>
                <History size={15} aria-hidden="true" />
                Versiones ({versiones.data?.length ?? 0})
              </Boton>
              <Boton variante="secundario" onClick={() => window.print()}>
                <Printer size={15} aria-hidden="true" />
                Exportar PDF
              </Boton>
              {puedeAprobar && (
                <select
                  aria-label="Estado del documento"
                  value={doc.estado}
                  onChange={(e) => cambiarEstado.mutate(e.target.value as EstadoDoc)}
                  className="h-8 rounded-[var(--radius-control)] border px-2 text-sm"
                  style={{
                    borderColor: "var(--border)",
                    backgroundColor: "var(--surface)",
                    color: "var(--fg)",
                  }}
                >
                  {ESTADOS_DOC.map((e) => (
                    <option key={e} value={e}>
                      {e}
                    </option>
                  ))}
                </select>
              )}
              {puedeEditar && (
                <>
                  <Boton variante="fantasma" onClick={() => setVerMetadatos(true)}>
                    <Settings2 size={15} aria-hidden="true" />
                    Portada
                  </Boton>
                  <Boton
                    variante="primario"
                    onClick={() => {
                      setBorrador(doc.cuerpo)
                      setEditando(true)
                    }}
                  >
                    <Pencil size={15} aria-hidden="true" />
                    Editar
                  </Boton>
                </>
              )}
              {puedeAprobar && doc.versiones_publicadas === 0 && (
                <Boton
                  variante="fantasma"
                  onClick={() => eliminar.mutate()}
                  disabled={eliminar.isPending}
                  style={{ color: "var(--destructive)" }}
                  aria-label="Eliminar el documento"
                >
                  <Trash2 size={15} aria-hidden="true" />
                </Boton>
              )}
            </>
          )}
        </div>
      </div>

      {(guardar.isError || cambiarEstado.isError || eliminar.isError) && (
        <p role="alert" className="no-print mb-4 text-sm" style={{ color: "var(--destructive)" }}>
          {[guardar.error, cambiarEstado.error, eliminar.error].find(
            (e) => e instanceof ErrorApi,
          ) instanceof ErrorApi
            ? (
                [guardar.error, cambiarEstado.error, eliminar.error].find(
                  (e) => e instanceof ErrorApi,
                ) as ErrorApi
              ).message
            : "No se pudo completar la operación"}
        </p>
      )}

      <div className="mx-auto max-w-[850px]">
        {/* El membrete de la agencia, antes de la portada del documento: un
            documento controlado sin marca de quien lo emite no esta controlado. */}
        <EncabezadoEmpresa titulo={doc.codigo} fecha={doc.fecha_emision ?? undefined} />
        <PortadaDocumento doc={doc} />
        {editando ? (
          <>
            <p className="no-print mb-3 text-xs" style={{ color: "var(--fg-muted)" }}>
              Usa <strong>Sección</strong> para cada apartado: se numeran solos. El salto de página
              fuerza hoja nueva al exportar.
            </p>
            <EditorDocumento valor={borrador ?? doc.cuerpo} alCambiar={setBorrador} />
          </>
        ) : (
          <Documento cuerpo={doc.cuerpo} />
        )}
      </div>

      {verMetadatos && (
        <MetadatosDocumento
          doc={doc}
          onCerrar={() => setVerMetadatos(false)}
          onGuardado={refrescar}
        />
      )}
      {verVersiones && (
        <VersionesDocumento
          documentoId={doc.id}
          version={doc.version}
          puedePublicar={puedeAprobar}
          onCerrar={() => setVerVersiones(false)}
        />
      )}
    </div>
  )
}
